import { CLASSIC_LEGACY_FORMAT } from './formats';

/**
 * TradeMagic Play constants (Asana 1220000146037011). The backend keeps a
 * same-named copy of every constant marked "shared"; change both or neither.
 */

/** Shared. Fixed first segments under /play. The invite generator never issues one of these,
 *  and the router resolves them before treating a segment as a code. */
export const PLAY_RESERVED_SEGMENTS = [
  'host',
  'me',
  'auth',
  'live',
  'new',
  'join',
  'profile',
  'settings',
  'admin',
  'api',
  'about',
  'help',
  'tournaments',
  'events',
  'standings',
  'pairings',
] as const;

/** Shared. Lowercase letters and digits excluding 0, o, 1, l, i. */
export const INVITE_CODE_LENGTH = 6;
export const INVITE_CODE_ALPHABET = 'abcdefghjkmnpqrstuvwxyz23456789';
export const INVITE_CODE_PATTERN = /^[abcdefghjkmnpqrstuvwxyz23456789]{6}$/;

export const isInviteCode = (segment: string | undefined): segment is string =>
  typeof segment === 'string' &&
  INVITE_CODE_PATTERN.test(segment) &&
  !(PLAY_RESERVED_SEGMENTS as readonly string[]).includes(segment);

/** Shared clock and rule constants (Decisions 7, 8, 9, 12, 17, 18). */
export const ROUND_DEFAULT_MINUTES = 50;
export const ROUND_MIN_MINUTES = 10;
export const ROUND_MAX_MINUTES = 90;
export const ROUND_MINUTES_STEP = 5;
export const ROUND_TIME_EXTENSION_MINUTES = 5;
export const ROUND_WARNING_SECONDS = 5 * 60;
export const SWISS_ROUNDS_MIN = 1;
export const SWISS_ROUNDS_MAX = 9;
export const LATE_REG_CUTOFF_ROUND = 2;
export const MIN_MAIN_CONSTRUCTED = 60;
export const MAX_SIDEBOARD = 15;
export const COMMANDER_DECK_SIZE = 100;

/** Shared. Reportable best-of-3 results as [mine, opponent]. 1-1 is not offered in elimination rounds. */
export const MATCH_RESULTS: ReadonlyArray<readonly [number, number]> = [
  [2, 0],
  [2, 1],
  [1, 1],
  [1, 2],
  [0, 2],
];

export type TournamentStructureValue = 'SWISS' | 'SWISS_TOP_CUT' | 'SINGLE_ELIM' | 'ROUND_ROBIN';

export const STRUCTURE_OPTIONS: ReadonlyArray<{ value: TournamentStructureValue; label: string; description: string }> = [
  { value: 'SWISS', label: 'Swiss', description: 'Everyone plays every round' },
  { value: 'SWISS_TOP_CUT', label: 'Swiss + top cut', description: 'Swiss, then playoffs' },
  { value: 'SINGLE_ELIM', label: 'Single elimination', description: "Lose once, you're out" },
  { value: 'ROUND_ROBIN', label: 'Round robin', description: 'Everyone plays everyone' },
];

/**
 * Shared (as PLAY_FORMAT_LABELS). The create payload sends the LABEL; `value` is
 * the lowercase search-filter value used only to deep-link ads into /search.
 * Alphabetical by label, Other last (Decision 7).
 */
export const PLAY_FORMAT_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'alchemy', label: 'Alchemy' },
  { value: 'brawl', label: 'Brawl' },
  { value: CLASSIC_LEGACY_FORMAT, label: 'Classic Legacy' },
  { value: 'commander', label: 'Commander' },
  { value: 'duel', label: 'Duel Commander' },
  { value: 'historic', label: 'Historic' },
  { value: 'legacy', label: 'Legacy' },
  { value: 'modern', label: 'Modern' },
  { value: 'oathbreaker', label: 'Oathbreaker' },
  { value: 'pauper', label: 'Pauper' },
  { value: 'paupercommander', label: 'Pauper Commander' },
  { value: 'penny', label: 'Penny Dreadful' },
  { value: 'pioneer', label: 'Pioneer' },
  { value: 'predh', label: 'PreDH' },
  { value: 'premodern', label: 'Premodern' },
  { value: 'standard', label: 'Standard' },
  { value: 'standardbrawl', label: 'Standard Brawl' },
  { value: 'timeless', label: 'Timeless' },
  { value: 'vintage', label: 'Vintage' },
];
export const PLAY_FORMAT_LABELS: readonly string[] = PLAY_FORMAT_OPTIONS.map((f) => f.label);
export const OTHER_FORMAT_LABEL = 'Other';

/** Shared. Formats whose lists skip the 60/15 count check (Decision 18; Brawl per ruling E11). */
export const COMMANDER_FAMILY_FORMATS: readonly string[] = [
  'Commander',
  'Duel Commander',
  'Pauper Commander',
  'PreDH',
  'Oathbreaker',
  'Brawl',
  'Standard Brawl',
];

export const formatSearchValue = (label: string): string | null =>
  PLAY_FORMAT_OPTIONS.find((f) => f.label === label)?.value ?? null;

/** Socket event names (backend Contract delta, section C). */
export const PLAY_SOCKET_EVENTS = {
  watch: 'play:watch',
  unwatch: 'play:unwatch',
  error: 'play:error',
  tournament: 'play:tournament',
  round: 'play:round',
  match: 'play:match',
  judge: 'play:judge',
  players: 'play:players',
  notify: 'play:notify',
} as const;

export const LIVE_POLL_INTERVAL_MS = 30_000;

/**
 * Ruling E4 deferred web push: no service worker, VAPID key or subscription
 * endpoint exists yet. The Browser push row is built behind this flag so it
 * ships the moment the backend does; it asks for the browser permission and
 * PUTs { push } once on.
 */
export const PLAY_BROWSER_PUSH_ENABLED = false;

export const DISCORD_BLURPLE = '#5865F2';

/** Decision 2: the product name, everywhere. */
export const PLAY_PRODUCT_NAME = 'TradeMagic Play';
export const PLAY_SITE_BASE = 'https://trademagic.gg';
